/**
 * Career graveyard — across-run record of every drafted Pokémon. Lives on
 * a separate `poke:career` localStorage key so it outlives Reset Run.
 *
 * Two write points only:
 *   - addEntry(entry) — DraftScene.commit at pick time.
 *   - markDied(id, diedTo, diedZone, levelAtDeath) — BattleScene at the
 *     |faint|p1 event, keyed by the PartyMember.id (which matches
 *     CareerEntry.id and pokedex.drafted snapshot id).
 *
 * Read paths:
 *   - findById(id) — used by PokedexScene's THIS RUN DRAFTED rows to
 *     JOIN status (alive/dead) onto the immutable pokedex snapshot.
 *   - getEntries() — full list (newest-first) for the CAREER tab's
 *     RECENT DEATHS section.
 *   - aggregateBySpecies() — Map keyed by species → { drafted, deaths }.
 *     Powers the BY POKÉMON section. v0.1 skips avgLifespan + ← best /
 *     ← lol annotations (the "battles per Pokémon" proxy is unstable
 *     without per-entry battle counts).
 *
 * Persistence: every mutation flushes via save(). Failures (quota,
 * private browsing) get logged but don't break gameplay — same posture
 * as saveManager.
 *
 * Schema versioning: { schemaVersion: 1, entries: CareerEntry[] }.
 * Future structural migration adds a parallel registry; v0.1 ships
 * single-version-only.
 */

import type { CaughtAt, Gender, IvSet } from './runState';
import type { BaseStats } from '../data/computeStat';
import type { Nature } from '../data/natures';

export interface CareerEntry {
  /** Matches PartyMember.id + pokedex.drafted.id for cross-collection
   *  JOIN at render time. */
  id: string;
  species: string;
  displayName: string;
  dexId: number;
  nature: Nature;
  ivs: IvSet;
  ability: string;
  /** Display name + short desc (denormalized from @pkmn/dex; PokedexScene
   *  RECENT DEATHS rows read this without an async dex roundtrip). */
  abilityName: string;
  abilityDesc: string;
  moves: string[];
  /** Title-case move display names aligned with moves[] by index. */
  movesDisplay: string[];
  /** Base stats for in-battle stat computation in PokemonFullSheet. */
  baseStats: BaseStats;
  /** Level at draft time (NEVER updated for level-ups — keeps the
   *  entry stable). Use levelAtDeath for the in-battle level. */
  level: number;
  draftedAt: number;
  draftedZone: string;
  diedAt: number | null;
  diedTo: string | null;
  diedZone: string | null;
  levelAtDeath: number | null;
  /** Cartridge-faithful gender, copied from DraftOffer at addEntry time.
   *  OPTIONAL — pre-v9 career entries (which DO survive the v8→v9
   *  save bump because careerStore is on a separate `poke:career` key
   *  with its own schemaVersion = 1 that does NOT bump) lack this
   *  field. PokedexScene renders missing values as the em-dash glyph
   *  in --text-tertiary. v9. */
  gender?: Gender;
  /** Where + when the Pokémon was originally drafted, copied from
   *  DraftOffer at addEntry time. OPTIONAL for the same pre-v9
   *  grace as gender — pre-PR-1 entries render with no "Caught:" line.
   *  Not a rename of `draftedAt: number` (epoch ms — kept untouched);
   *  `caughtAt` is thematically distinct + matches the user's prompt
   *  language. v9. */
  caughtAt?: CaughtAt;
}

interface CareerStoreV1 {
  schemaVersion: 1;
  entries: CareerEntry[];
}

const STORAGE_KEY = 'poke:career';
const SCHEMA_VERSION = 1;

function freshState(): CareerStoreV1 {
  return { schemaVersion: SCHEMA_VERSION, entries: [] };
}

class CareerStore {
  private state: CareerStoreV1 = freshState();
  /** id → index into entries[] for O(1) lookups. Rebuilt on load. */
  private indexById = new Map<string, number>();

  /** Read from localStorage. Called at game boot from main.ts. Silent
   *  fall-through to fresh state on missing / corrupt / future-version
   *  data — career data is non-critical (gameplay works without it). */
  load(): void {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      console.warn('[career] read failed:', err);
    }
    if (!raw) {
      this.state = freshState();
      this.rebuildIndex();
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      console.warn('[career] corrupt JSON, dropping:', err);
      this.state = freshState();
      this.rebuildIndex();
      return;
    }
    const obj = parsed as Partial<CareerStoreV1>;
    if (obj.schemaVersion === SCHEMA_VERSION && Array.isArray(obj.entries)) {
      this.state = { schemaVersion: SCHEMA_VERSION, entries: obj.entries as CareerEntry[] };
    } else {
      console.warn('[career] unknown schema, dropping');
      this.state = freshState();
    }
    this.rebuildIndex();
  }

  /** Flush to localStorage. Called after every mutation. */
  save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (err) {
      console.warn('[career] write failed:', err);
    }
  }

  /** Add a new entry at draft pick time. The id MUST match the
   *  corresponding PartyMember.id + pokedex.drafted snapshot id. */
  addEntry(entry: CareerEntry): void {
    this.state.entries.push({
      ...entry,
      ivs: { ...entry.ivs },
      moves: [...entry.moves],
      movesDisplay: [...entry.movesDisplay],
      baseStats: { ...entry.baseStats },
      caughtAt: entry.caughtAt ? { ...entry.caughtAt } : undefined,
    });
    this.indexById.set(entry.id, this.state.entries.length - 1);
    this.save();
  }

  /** Mark a previously-added entry as dead. Updates diedAt/diedTo/
   *  diedZone/levelAtDeath in-place. No-op if id not found (defensive —
   *  the design doc's defensive invariant for sim/career write race). */
  markDied(args: {
    id: string;
    diedTo: string;
    diedZone: string;
    levelAtDeath: number;
  }): void {
    const idx = this.indexById.get(args.id);
    if (idx === undefined) return;
    const entry = this.state.entries[idx];
    if (!entry) return;
    entry.diedAt = Date.now();
    entry.diedTo = args.diedTo;
    entry.diedZone = args.diedZone;
    entry.levelAtDeath = args.levelAtDeath;
    this.save();
  }

  findById(id: string): CareerEntry | undefined {
    const idx = this.indexById.get(id);
    if (idx === undefined) return undefined;
    const entry = this.state.entries[idx];
    if (!entry) return undefined;
    return {
      ...entry,
      ivs: { ...entry.ivs },
      moves: [...entry.moves],
      movesDisplay: [...(entry.movesDisplay ?? [])],
      baseStats: { ...(entry.baseStats ?? { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }) },
      caughtAt: entry.caughtAt ? { ...entry.caughtAt } : undefined,
    };
  }

  /** Full entries list, newest-first by draftedAt (the natural order of
   *  insertion since draftedAt is set at push time). Returns deep copies
   *  so caller mutations don't bleed back into internal state. */
  getEntries(): CareerEntry[] {
    const copies = this.state.entries.map((e) => ({
      ...e,
      ivs: { ...e.ivs },
      moves: [...e.moves],
      movesDisplay: [...(e.movesDisplay ?? [])],
      baseStats: { ...(e.baseStats ?? { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }) },
      caughtAt: e.caughtAt ? { ...e.caughtAt } : undefined,
    }));
    // Newest first — reverse the push-order array.
    return copies.reverse();
  }

  /** Aggregate per species: how many drafted + how many died. Powers the
   *  CAREER tab BY POKÉMON section. avgLifespan deferred (no battle-
   *  count tracking in v0.1). */
  aggregateBySpecies(): Map<string, { drafted: number; deaths: number }> {
    const out = new Map<string, { drafted: number; deaths: number }>();
    for (const e of this.state.entries) {
      const slot = out.get(e.species) ?? { drafted: 0, deaths: 0 };
      slot.drafted += 1;
      if (e.diedAt !== null) slot.deaths += 1;
      out.set(e.species, slot);
    }
    return out;
  }

  /** Total counts for the CAREER tab header. */
  getTotals(): { drafts: number; deaths: number } {
    let drafts = 0;
    let deaths = 0;
    for (const e of this.state.entries) {
      drafts += 1;
      if (e.diedAt !== null) deaths += 1;
    }
    return { drafts, deaths };
  }

  /** Pure test helper — wipe in-memory + storage. Don't expose to UX
   *  surfaces; Reset Run is intentionally scoped to runState only. */
  reset(): void {
    this.state = freshState();
    this.indexById.clear();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private rebuildIndex(): void {
    this.indexById.clear();
    for (let i = 0; i < this.state.entries.length; i++) {
      this.indexById.set(this.state.entries[i]!.id, i);
    }
  }
}

export const careerStore = new CareerStore();
